/**
 * Topographic contour-line background.
 * Renders a deterministic-yet-organic set of nested closed contour paths.
 *
 * Props:
 *  - variant: "light" | "dark" — color scheme
 *  - opacity: 0..1 multiplier
 *  - seed: integer to vary the contour positions per page
 *
 * Pure SVG, no external deps. Sits absolutely positioned inside a relative parent.
 */
export default function ContourBackground({ variant = "light", opacity = 0.5, seed = 1, className = "" }) {
  const stroke = variant === "dark" ? "rgba(255,255,255,0.18)" : "rgba(15,23,42,0.16)";
  const strokeHi = variant === "dark" ? "rgba(16,185,129,0.35)" : "rgba(6,95,70,0.22)";

  // Two "hill" centers shifted by seed
  const c1x = 240 + ((seed * 37) % 220);
  const c1y = 180 + ((seed * 53) % 120);
  const c2x = 900 + ((seed * 71) % 260);
  const c2y = 340 + ((seed * 29) % 160);

  // 8 nested ellipses per hill
  const rings = Array.from({ length: 9 }, (_, i) => i + 1);

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      style={{ opacity }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1440 720"
        preserveAspectRatio="xMidYMid slice"
        className="w-full h-full"
      >
        {/* Hill 1 — wider ellipses */}
        {rings.map((r) => (
          <ellipse
            key={`a${r}`}
            cx={c1x}
            cy={c1y}
            rx={70 + r * 38}
            ry={42 + r * 22}
            fill="none"
            stroke={r === 4 || r === 7 ? strokeHi : stroke}
            strokeWidth={r === 4 || r === 7 ? 1.1 : 0.7}
            transform={`rotate(${(seed * 7) % 25} ${c1x} ${c1y})`}
          />
        ))}
        {/* Hill 2 — tighter, rotated */}
        {rings.map((r) => (
          <ellipse
            key={`b${r}`}
            cx={c2x}
            cy={c2y}
            rx={56 + r * 30}
            ry={50 + r * 26}
            fill="none"
            stroke={r === 3 || r === 6 ? strokeHi : stroke}
            strokeWidth={r === 3 || r === 6 ? 1.1 : 0.7}
            transform={`rotate(${-15 - ((seed * 11) % 20)} ${c2x} ${c2y})`}
          />
        ))}

        {/* Survey crosshairs */}
        <g stroke={stroke} strokeWidth="0.5">
          <line x1={c1x} y1={c1y - 8} x2={c1x} y2={c1y + 8} />
          <line x1={c1x - 8} y1={c1y} x2={c1x + 8} y2={c1y} />
          <line x1={c2x} y1={c2y - 8} x2={c2x} y2={c2y + 8} />
          <line x1={c2x - 8} y1={c2y} x2={c2x + 8} y2={c2y} />
        </g>
      </svg>
    </div>
  );
}
